import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
  AuthAccessTokenGet200,
  BadRequestResponse,
  BearerDTO,
  BearersFindParams,
  ForbiddenResponse,
  FoundResponse,
  GrantedOfferingDTO,
  GrantedOfferingsListParams,
  Health200,
  InternalServerErrorResponse,
  NotFoundResponse,
  NotImplementedResponse,
  OfferingCreateDTO,
  OfferingDTO,
  OfferingGrantDTO,
  OfferingGrantResponseDTO,
  OfferingNotifyDTO,
  OfferingNotifyResponseDTO,
  OfferingUpdateDTO,
  OfferingValidateRecipientDTO,
  OfferingValidateRecipientResponseDTO,
  OfferingWithBobcatOffersDTO,
  OfferingsListParams,
  ResellerCreateDTO,
  ResellerDTO,
  ResellerUpdateDTO,
  ServiceUnavailableResponse,
  TravellerDTO,
  TravellersFindParams,
  UnauthorizedResponse,
  UnprocessableEntityResponse,
  UserPersonaDTO,
} from './models';

import { useAxiosReseller } from '../../hooks/useAxiosReseller';
import type { ErrorType } from '../../hooks/useAxiosReseller';
/**
 * Health check used by the Kubernetes readiness and liveness probes. No auth.
 */
export const useHealthHook = () => {
  const health = useAxiosReseller<Health200>();

  return useCallback(
    (signal?: AbortSignal) => {
      return health({ url: `/health`, method: 'GET', signal });
    },
    [health],
  );
};

export const getHealthQueryKey = () => {
  return [`/health`] as const;
};

export const useHealthQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthQueryKey();

  const health = useHealthHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useHealthHook>>>
  > = ({ signal }) => health(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type HealthQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useHealthHook>>>
>;
export type HealthQueryError = ErrorType<unknown>;

export function useHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useHealthHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useHealthHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useHealthHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useHealthQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Payment terminal view
 */
export const useTerminalPaymentTransactionHook = () => {
  const terminalPaymentTransaction = useAxiosReseller<unknown>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return terminalPaymentTransaction({
        url: `/terminals/payment_transaction/${id}`,
        method: 'GET',
        signal,
      });
    },
    [terminalPaymentTransaction],
  );
};

export const getTerminalPaymentTransactionQueryKey = (id?: string) => {
  return [`/terminals/payment_transaction/${id}`] as const;
};

export const useTerminalPaymentTransactionQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
  >,
  TError = ErrorType<
    FoundResponse | UnprocessableEntityResponse | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getTerminalPaymentTransactionQueryKey(id);

  const terminalPaymentTransaction = useTerminalPaymentTransactionHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>>
  > = ({ signal }) => terminalPaymentTransaction(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type TerminalPaymentTransactionQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>>
>;
export type TerminalPaymentTransactionQueryError = ErrorType<
  FoundResponse | UnprocessableEntityResponse | InternalServerErrorResponse
>;

export function useTerminalPaymentTransaction<
  TData = Awaited<
    ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
  >,
  TError = ErrorType<
    FoundResponse | UnprocessableEntityResponse | InternalServerErrorResponse
  >,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useTerminalPaymentTransaction<
  TData = Awaited<
    ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
  >,
  TError = ErrorType<
    FoundResponse | UnprocessableEntityResponse | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useTerminalPaymentTransaction<
  TData = Awaited<
    ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
  >,
  TError = ErrorType<
    FoundResponse | UnprocessableEntityResponse | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useTerminalPaymentTransaction<
  TData = Awaited<
    ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
  >,
  TError = ErrorType<
    FoundResponse | UnprocessableEntityResponse | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useTerminalPaymentTransactionHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useTerminalPaymentTransactionQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List available personas for identity
 */
export const useAuthAvailablePersonasListHook = () => {
  const authAvailablePersonasList = useAxiosReseller<UserPersonaDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return authAvailablePersonasList({
        url: `/auth/available-personas`,
        method: 'GET',
        signal,
      });
    },
    [authAvailablePersonasList],
  );
};

export const getAuthAvailablePersonasListQueryKey = () => {
  return [`/auth/available-personas`] as const;
};

export const useAuthAvailablePersonasListQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAuthAvailablePersonasListQueryKey();

  const authAvailablePersonasList = useAuthAvailablePersonasListHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>>
  > = ({ signal }) => authAvailablePersonasList(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type AuthAvailablePersonasListQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>>
>;
export type AuthAvailablePersonasListQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
>;

export function useAuthAvailablePersonasList<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useAuthAvailablePersonasList<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useAuthAvailablePersonasList<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useAuthAvailablePersonasList<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
  >,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthAvailablePersonasListHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useAuthAvailablePersonasListQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get Access token for Reseller
 */
export const useAuthAccessTokenGetHook = () => {
  const authAccessTokenGet = useAxiosReseller<AuthAccessTokenGet200>();

  return useCallback(
    (resellerId: string, signal?: AbortSignal) => {
      return authAccessTokenGet({
        url: `/auth/access-token/${resellerId}`,
        method: 'GET',
        signal,
      });
    },
    [authAccessTokenGet],
  );
};

export const getAuthAccessTokenGetQueryKey = (resellerId?: string) => {
  return [`/auth/access-token/${resellerId}`] as const;
};

export const useAuthAccessTokenGetQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  resellerId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAuthAccessTokenGetQueryKey(resellerId);

  const authAccessTokenGet = useAuthAccessTokenGetHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>
  > = ({ signal }) => authAccessTokenGet(resellerId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!resellerId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type AuthAccessTokenGetQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>
>;
export type AuthAccessTokenGetQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useAuthAccessTokenGet<
  TData = Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  resellerId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useAuthAccessTokenGet<
  TData = Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  resellerId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useAuthAccessTokenGet<
  TData = Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  resellerId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useAuthAccessTokenGet<
  TData = Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  resellerId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useAuthAccessTokenGetHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useAuthAccessTokenGetQueryOptions(resellerId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Start login flow for managing self-service offerings
 */
export const useSelfserviceLoginHook = () => {
  const selfserviceLogin = useAxiosReseller<unknown>();

  return useCallback(
    (slug: string, signal?: AbortSignal) => {
      return selfserviceLogin({
        url: `/selfservice/offerings/${slug}`,
        method: 'GET',
        signal,
      });
    },
    [selfserviceLogin],
  );
};

export const getSelfserviceLoginQueryKey = (slug?: string) => {
  return [`/selfservice/offerings/${slug}`] as const;
};

export const useSelfserviceLoginQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
  TError = ErrorType<
    | FoundResponse
    | BadRequestResponse
    | NotFoundResponse
    | InternalServerErrorResponse
    | NotImplementedResponse
    | ServiceUnavailableResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSelfserviceLoginQueryKey(slug);

  const selfserviceLogin = useSelfserviceLoginHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>
  > = ({ signal }) => selfserviceLogin(slug, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type SelfserviceLoginQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>
>;
export type SelfserviceLoginQueryError = ErrorType<
  | FoundResponse
  | BadRequestResponse
  | NotFoundResponse
  | InternalServerErrorResponse
  | NotImplementedResponse
  | ServiceUnavailableResponse
>;

export function useSelfserviceLogin<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
  TError = ErrorType<
    | FoundResponse
    | BadRequestResponse
    | NotFoundResponse
    | InternalServerErrorResponse
    | NotImplementedResponse
    | ServiceUnavailableResponse
  >,
>(
  slug: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useSelfserviceLogin<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
  TError = ErrorType<
    | FoundResponse
    | BadRequestResponse
    | NotFoundResponse
    | InternalServerErrorResponse
    | NotImplementedResponse
    | ServiceUnavailableResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSelfserviceLogin<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
  TError = ErrorType<
    | FoundResponse
    | BadRequestResponse
    | NotFoundResponse
    | InternalServerErrorResponse
    | NotImplementedResponse
    | ServiceUnavailableResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useSelfserviceLogin<
  TData = Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
  TError = ErrorType<
    | FoundResponse
    | BadRequestResponse
    | NotFoundResponse
    | InternalServerErrorResponse
    | NotImplementedResponse
    | ServiceUnavailableResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSelfserviceLoginHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useSelfserviceLoginQueryOptions(slug, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List available resellers
 */
export const useResellersListHook = () => {
  const resellersList = useAxiosReseller<ResellerDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return resellersList({ url: `/resellers`, method: 'GET', signal });
    },
    [resellersList],
  );
};

export const getResellersListQueryKey = () => {
  return [`/resellers`] as const;
};

export const useResellersListQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getResellersListQueryKey();

  const resellersList = useResellersListHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>
  > = ({ signal }) => resellersList(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ResellersListQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>
>;
export type ResellersListQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
>;

export function useResellersList<
  TData = Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useResellersList<
  TData = Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useResellersList<
  TData = Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useResellersList<
  TData = Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | InternalServerErrorResponse
  >,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellersListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useResellersListQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new reseller
 */
export const useResellerCreateHook = () => {
  const resellerCreate = useAxiosReseller<ResellerDTO>();

  return useCallback(
    (resellerCreateDTO: ResellerCreateDTO, signal?: AbortSignal) => {
      return resellerCreate({
        url: `/resellers`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: resellerCreateDTO,
        signal,
      });
    },
    [resellerCreate],
  );
};

export const useResellerCreateMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useResellerCreateHook>>>,
    TError,
    { data: ResellerCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useResellerCreateHook>>>,
  TError,
  { data: ResellerCreateDTO },
  TContext
> => {
  const mutationKey = ['resellerCreate'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const resellerCreate = useResellerCreateHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useResellerCreateHook>>>,
    { data: ResellerCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return resellerCreate(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResellerCreateMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useResellerCreateHook>>>
>;
export type ResellerCreateMutationBody = ResellerCreateDTO;
export type ResellerCreateMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useResellerCreate = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useResellerCreateHook>>>,
      TError,
      { data: ResellerCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useResellerCreateHook>>>,
  TError,
  { data: ResellerCreateDTO },
  TContext
> => {
  const mutationOptions = useResellerCreateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get a reseller
 */
export const useResellerGetHook = () => {
  const resellerGet = useAxiosReseller<ResellerDTO>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return resellerGet({ url: `/resellers/${id}`, method: 'GET', signal });
    },
    [resellerGet],
  );
};

export const getResellerGetQueryKey = (id?: string) => {
  return [`/resellers/${id}`] as const;
};

export const useResellerGetQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getResellerGetQueryKey(id);

  const resellerGet = useResellerGetHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>
  > = ({ signal }) => resellerGet(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ResellerGetQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>
>;
export type ResellerGetQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useResellerGet<
  TData = Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useResellerGet<
  TData = Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useResellerGet<
  TData = Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useResellerGet<
  TData = Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useResellerGetHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useResellerGetQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update a reseller
 */
export const useResellerUpdateHook = () => {
  const resellerUpdate = useAxiosReseller<ResellerDTO>();

  return useCallback(
    (id: string, resellerUpdateDTO: ResellerUpdateDTO) => {
      return resellerUpdate({
        url: `/resellers/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: resellerUpdateDTO,
      });
    },
    [resellerUpdate],
  );
};

export const useResellerUpdateMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useResellerUpdateHook>>>,
    TError,
    { id: string; data: ResellerUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useResellerUpdateHook>>>,
  TError,
  { id: string; data: ResellerUpdateDTO },
  TContext
> => {
  const mutationKey = ['resellerUpdate'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const resellerUpdate = useResellerUpdateHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useResellerUpdateHook>>>,
    { id: string; data: ResellerUpdateDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return resellerUpdate(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResellerUpdateMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useResellerUpdateHook>>>
>;
export type ResellerUpdateMutationBody = ResellerUpdateDTO;
export type ResellerUpdateMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useResellerUpdate = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useResellerUpdateHook>>>,
      TError,
      { id: string; data: ResellerUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useResellerUpdateHook>>>,
  TError,
  { id: string; data: ResellerUpdateDTO },
  TContext
> => {
  const mutationOptions = useResellerUpdateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Delete a reseller
 */
export const useResellerDeleteHook = () => {
  const resellerDelete = useAxiosReseller<void>();

  return useCallback(
    (id: string) => {
      return resellerDelete({ url: `/resellers/${id}`, method: 'DELETE' });
    },
    [resellerDelete],
  );
};

export const useResellerDeleteMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useResellerDeleteHook>>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useResellerDeleteHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['resellerDelete'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const resellerDelete = useResellerDeleteHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useResellerDeleteHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return resellerDelete(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResellerDeleteMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useResellerDeleteHook>>>
>;

export type ResellerDeleteMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useResellerDelete = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useResellerDeleteHook>>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useResellerDeleteHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = useResellerDeleteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List available offerings
 */
export const useOfferingsListHook = () => {
  const offeringsList = useAxiosReseller<OfferingDTO>();

  return useCallback(
    (params?: OfferingsListParams, signal?: AbortSignal) => {
      return offeringsList({
        url: `/offerings`,
        method: 'GET',
        params,
        signal,
      });
    },
    [offeringsList],
  );
};

export const getOfferingsListQueryKey = (params?: OfferingsListParams) => {
  return [`/offerings`, ...(params ? [params] : [])] as const;
};

export const useOfferingsListQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: OfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getOfferingsListQueryKey(params);

  const offeringsList = useOfferingsListHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>
  > = ({ signal }) => offeringsList(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type OfferingsListQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>
>;
export type OfferingsListQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: undefined | OfferingsListParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: OfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: OfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: OfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useOfferingsListQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new offering
 */
export const useOfferingCreateHook = () => {
  const offeringCreate = useAxiosReseller<OfferingDTO>();

  return useCallback(
    (offeringCreateDTO: OfferingCreateDTO, signal?: AbortSignal) => {
      return offeringCreate({
        url: `/offerings`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: offeringCreateDTO,
        signal,
      });
    },
    [offeringCreate],
  );
};

export const useOfferingCreateMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingCreateHook>>>,
    TError,
    { data: OfferingCreateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useOfferingCreateHook>>>,
  TError,
  { data: OfferingCreateDTO },
  TContext
> => {
  const mutationKey = ['offeringCreate'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const offeringCreate = useOfferingCreateHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingCreateHook>>>,
    { data: OfferingCreateDTO }
  > = (props) => {
    const { data } = props ?? {};

    return offeringCreate(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type OfferingCreateMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingCreateHook>>>
>;
export type OfferingCreateMutationBody = OfferingCreateDTO;
export type OfferingCreateMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useOfferingCreate = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useOfferingCreateHook>>>,
      TError,
      { data: OfferingCreateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useOfferingCreateHook>>>,
  TError,
  { data: OfferingCreateDTO },
  TContext
> => {
  const mutationOptions = useOfferingCreateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get an offering
 */
export const useOfferingGetHook = () => {
  const offeringGet = useAxiosReseller<OfferingWithBobcatOffersDTO>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return offeringGet({ url: `/offerings/${id}`, method: 'GET', signal });
    },
    [offeringGet],
  );
};

export const getOfferingGetQueryKey = (id?: string) => {
  return [`/offerings/${id}`] as const;
};

export const useOfferingGetQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getOfferingGetQueryKey(id);

  const offeringGet = useOfferingGetHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>
  > = ({ signal }) => offeringGet(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type OfferingGetQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>
>;
export type OfferingGetQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useOfferingGet<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useOfferingGet<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useOfferingGet<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useOfferingGet<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useOfferingGetQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update an offering
 */
export const useOfferingUpdateHook = () => {
  const offeringUpdate = useAxiosReseller<OfferingDTO>();

  return useCallback(
    (id: string, offeringUpdateDTO: OfferingUpdateDTO) => {
      return offeringUpdate({
        url: `/offerings/${id}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: offeringUpdateDTO,
      });
    },
    [offeringUpdate],
  );
};

export const useOfferingUpdateMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingUpdateHook>>>,
    TError,
    { id: string; data: OfferingUpdateDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useOfferingUpdateHook>>>,
  TError,
  { id: string; data: OfferingUpdateDTO },
  TContext
> => {
  const mutationKey = ['offeringUpdate'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const offeringUpdate = useOfferingUpdateHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingUpdateHook>>>,
    { id: string; data: OfferingUpdateDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return offeringUpdate(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type OfferingUpdateMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingUpdateHook>>>
>;
export type OfferingUpdateMutationBody = OfferingUpdateDTO;
export type OfferingUpdateMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useOfferingUpdate = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useOfferingUpdateHook>>>,
      TError,
      { id: string; data: OfferingUpdateDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useOfferingUpdateHook>>>,
  TError,
  { id: string; data: OfferingUpdateDTO },
  TContext
> => {
  const mutationOptions = useOfferingUpdateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Delete an offering
 */
export const useOfferingDeleteHook = () => {
  const offeringDelete = useAxiosReseller<void>();

  return useCallback(
    (id: string) => {
      return offeringDelete({ url: `/offerings/${id}`, method: 'DELETE' });
    },
    [offeringDelete],
  );
};

export const useOfferingDeleteMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingDeleteHook>>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useOfferingDeleteHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['offeringDelete'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const offeringDelete = useOfferingDeleteHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingDeleteHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return offeringDelete(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type OfferingDeleteMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingDeleteHook>>>
>;

export type OfferingDeleteMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useOfferingDelete = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useOfferingDeleteHook>>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useOfferingDeleteHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = useOfferingDeleteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Grant an offer to a traveller
 */
export const useOfferingGrantHook = () => {
  const offeringGrant = useAxiosReseller<OfferingGrantResponseDTO>();

  return useCallback(
    (id: string, offeringGrantDTO: OfferingGrantDTO, signal?: AbortSignal) => {
      return offeringGrant({
        url: `/offerings/${id}/grant`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: offeringGrantDTO,
        signal,
      });
    },
    [offeringGrant],
  );
};

export const useOfferingGrantMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingGrantHook>>>,
    TError,
    { id: string; data: OfferingGrantDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useOfferingGrantHook>>>,
  TError,
  { id: string; data: OfferingGrantDTO },
  TContext
> => {
  const mutationKey = ['offeringGrant'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const offeringGrant = useOfferingGrantHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingGrantHook>>>,
    { id: string; data: OfferingGrantDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return offeringGrant(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type OfferingGrantMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingGrantHook>>>
>;
export type OfferingGrantMutationBody = OfferingGrantDTO;
export type OfferingGrantMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useOfferingGrant = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useOfferingGrantHook>>>,
      TError,
      { id: string; data: OfferingGrantDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useOfferingGrantHook>>>,
  TError,
  { id: string; data: OfferingGrantDTO },
  TContext
> => {
  const mutationOptions = useOfferingGrantMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Check whether a recipient's Swedish personnummer satisfies the
offering's SPAR residency requirements. On success, returns a
short-lived signed token that must be passed back as `spar_token`
on the subsequent offeringGrant request, avoiding a second SPAR
lookup at grant time. The token is valid for 15 minutes and is
bound to the (offering_id, recipient_nin) pair.

If the offering has no SPAR region restriction, returns
`{ eligible: true }` with no token — the grant call does not
require `spar_token` in that case.

 */
export const useOfferingValidateRecipientHook = () => {
  const offeringValidateRecipient =
    useAxiosReseller<OfferingValidateRecipientResponseDTO>();

  return useCallback(
    (
      id: string,
      offeringValidateRecipientDTO: OfferingValidateRecipientDTO,
      signal?: AbortSignal,
    ) => {
      return offeringValidateRecipient({
        url: `/offerings/${id}/validate-recipient`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: offeringValidateRecipientDTO,
        signal,
      });
    },
    [offeringValidateRecipient],
  );
};

export const useOfferingValidateRecipientMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingValidateRecipientHook>>>,
    TError,
    { id: string; data: OfferingValidateRecipientDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useOfferingValidateRecipientHook>>>,
  TError,
  { id: string; data: OfferingValidateRecipientDTO },
  TContext
> => {
  const mutationKey = ['offeringValidateRecipient'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const offeringValidateRecipient = useOfferingValidateRecipientHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingValidateRecipientHook>>>,
    { id: string; data: OfferingValidateRecipientDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return offeringValidateRecipient(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type OfferingValidateRecipientMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingValidateRecipientHook>>>
>;
export type OfferingValidateRecipientMutationBody =
  OfferingValidateRecipientDTO;
export type OfferingValidateRecipientMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useOfferingValidateRecipient = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useOfferingValidateRecipientHook>>>,
      TError,
      { id: string; data: OfferingValidateRecipientDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useOfferingValidateRecipientHook>>>,
  TError,
  { id: string; data: OfferingValidateRecipientDTO },
  TContext
> => {
  const mutationOptions = useOfferingValidateRecipientMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Send a selfservice confirmation email without issuing a ticket.
Used when the user has no bearer and needs to pick up a physical
card from customer service.

 */
export const useOfferingNotifyHook = () => {
  const offeringNotify = useAxiosReseller<OfferingNotifyResponseDTO>();

  return useCallback(
    (
      id: string,
      offeringNotifyDTO: OfferingNotifyDTO,
      signal?: AbortSignal,
    ) => {
      return offeringNotify({
        url: `/offerings/${id}/notify`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: offeringNotifyDTO,
        signal,
      });
    },
    [offeringNotify],
  );
};

export const useOfferingNotifyMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingNotifyHook>>>,
    TError,
    { id: string; data: OfferingNotifyDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useOfferingNotifyHook>>>,
  TError,
  { id: string; data: OfferingNotifyDTO },
  TContext
> => {
  const mutationKey = ['offeringNotify'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const offeringNotify = useOfferingNotifyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingNotifyHook>>>,
    { id: string; data: OfferingNotifyDTO }
  > = (props) => {
    const { id, data } = props ?? {};

    return offeringNotify(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type OfferingNotifyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingNotifyHook>>>
>;
export type OfferingNotifyMutationBody = OfferingNotifyDTO;
export type OfferingNotifyMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useOfferingNotify = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useOfferingNotifyHook>>>,
      TError,
      { id: string; data: OfferingNotifyDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useOfferingNotifyHook>>>,
  TError,
  { id: string; data: OfferingNotifyDTO },
  TContext
> => {
  const mutationOptions = useOfferingNotifyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Get an offering by slug
 */
export const useOfferingGetBySlugHook = () => {
  const offeringGetBySlug = useAxiosReseller<OfferingWithBobcatOffersDTO>();

  return useCallback(
    (slug: string, signal?: AbortSignal) => {
      return offeringGetBySlug({
        url: `/public/offerings/slug/${slug}`,
        method: 'GET',
        signal,
      });
    },
    [offeringGetBySlug],
  );
};

export const getOfferingGetBySlugQueryKey = (slug?: string) => {
  return [`/public/offerings/slug/${slug}`] as const;
};

export const useOfferingGetBySlugQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getOfferingGetBySlugQueryKey(slug);

  const offeringGetBySlug = useOfferingGetBySlugHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>
  > = ({ signal }) => offeringGetBySlug(slug, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type OfferingGetBySlugQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>
>;
export type OfferingGetBySlugQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useOfferingGetBySlug<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  slug: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useOfferingGetBySlug<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useOfferingGetBySlug<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useOfferingGetBySlug<
  TData = Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOfferingGetBySlugHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useOfferingGetBySlugQueryOptions(slug, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List granted offerings
 */
export const useGrantedOfferingsListHook = () => {
  const grantedOfferingsList = useAxiosReseller<GrantedOfferingDTO>();

  return useCallback(
    (params?: GrantedOfferingsListParams, signal?: AbortSignal) => {
      return grantedOfferingsList({
        url: `/granted_offerings`,
        method: 'GET',
        params,
        signal,
      });
    },
    [grantedOfferingsList],
  );
};

export const getGrantedOfferingsListQueryKey = (
  params?: GrantedOfferingsListParams,
) => {
  return [`/granted_offerings`, ...(params ? [params] : [])] as const;
};

export const useGrantedOfferingsListQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: GrantedOfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGrantedOfferingsListQueryKey(params);

  const grantedOfferingsList = useGrantedOfferingsListHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>
  > = ({ signal }) => grantedOfferingsList(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GrantedOfferingsListQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>
>;
export type GrantedOfferingsListQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useGrantedOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: undefined | GrantedOfferingsListParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGrantedOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: GrantedOfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGrantedOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: GrantedOfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useGrantedOfferingsList<
  TData = Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params?: GrantedOfferingsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGrantedOfferingsListHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGrantedOfferingsListQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Revokes a granted offering
 */
export const useGrantedOfferingDeleteHook = () => {
  const grantedOfferingDelete = useAxiosReseller<GrantedOfferingDTO>();

  return useCallback(
    (id: string) => {
      return grantedOfferingDelete({
        url: `/granted_offerings/${id}`,
        method: 'DELETE',
      });
    },
    [grantedOfferingDelete],
  );
};

export const useGrantedOfferingDeleteMutationOptions = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useGrantedOfferingDeleteHook>>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useGrantedOfferingDeleteHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['grantedOfferingDelete'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const grantedOfferingDelete = useGrantedOfferingDeleteHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useGrantedOfferingDeleteHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return grantedOfferingDelete(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type GrantedOfferingDeleteMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGrantedOfferingDeleteHook>>>
>;

export type GrantedOfferingDeleteMutationError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export const useGrantedOfferingDelete = <
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useGrantedOfferingDeleteHook>>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useGrantedOfferingDeleteHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = useGrantedOfferingDeleteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * List travellers for email
 */
export const useTravellersFindHook = () => {
  const travellersFind = useAxiosReseller<TravellerDTO[]>();

  return useCallback(
    (params: TravellersFindParams, signal?: AbortSignal) => {
      return travellersFind({
        url: `/travellers`,
        method: 'GET',
        params,
        signal,
      });
    },
    [travellersFind],
  );
};

export const getTravellersFindQueryKey = (params?: TravellersFindParams) => {
  return [`/travellers`, ...(params ? [params] : [])] as const;
};

export const useTravellersFindQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: TravellersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getTravellersFindQueryKey(params);

  const travellersFind = useTravellersFindHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>
  > = ({ signal }) => travellersFind(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type TravellersFindQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>
>;
export type TravellersFindQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useTravellersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: TravellersFindParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useTravellersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: TravellersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useTravellersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: TravellersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useTravellersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: TravellersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useTravellersFindHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useTravellersFindQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get bearer for card serial
 */
export const useBearersFindHook = () => {
  const bearersFind = useAxiosReseller<BearerDTO>();

  return useCallback(
    (params: BearersFindParams, signal?: AbortSignal) => {
      return bearersFind({ url: `/bearers`, method: 'GET', params, signal });
    },
    [bearersFind],
  );
};

export const getBearersFindQueryKey = (params?: BearersFindParams) => {
  return [`/bearers`, ...(params ? [params] : [])] as const;
};

export const useBearersFindQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: BearersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBearersFindQueryKey(params);

  const bearersFind = useBearersFindHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>
  > = ({ signal }) => bearersFind(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type BearersFindQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>
>;
export type BearersFindQueryError = ErrorType<
  | UnauthorizedResponse
  | ForbiddenResponse
  | UnprocessableEntityResponse
  | InternalServerErrorResponse
>;

export function useBearersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: BearersFindParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useBearersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: BearersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useBearersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: BearersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useBearersFind<
  TData = Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
  TError = ErrorType<
    | UnauthorizedResponse
    | ForbiddenResponse
    | UnprocessableEntityResponse
    | InternalServerErrorResponse
  >,
>(
  params: BearersFindParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useBearersFindHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useBearersFindQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
